import { inject, Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Route, Router, Routes } from '@angular/router';
import { DynamicRoute } from '../interfaces/dynamic-route';
import { IDynamicRoutesProvider } from '../providers/dynamic-route.provider';
import { CORE_APP_CONFIG } from '../utils/config.constants';
import { CoreConfig } from '../interfaces/core-config';
import { catchError, map, Observable, of, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DynamicRouteService {
    private _config: CoreConfig = inject(CORE_APP_CONFIG);
    
    constructor(
        private http: HttpClient,
        private router: Router,
        @Inject(IDynamicRoutesProvider.getToken())
        private dynamicRoutesProviderList: IDynamicRoutesProvider[],
    ) { }

    // API call to retrieve the list of paths
    loadRoutes(): Observable<any> {
        console.log("loadRoutes()");
        return this.http.get<DynamicRoute[]>('api/navigation/routes').pipe(
            tap(responseRoutes => {
                console.log("loadRoutes()",responseRoutes);
                const dynamicRoutes: Routes = [];

                for (const providerRoute of this.dynamicRoutesProviderList) {
                    // Recupero solo l'obj interessato per il provider
                    for(const rRoute of responseRoutes) {
                        // Controllo se l'id corrisponde con la describe del provider
                        if(rRoute.id == providerRoute.describe()) {
                            // Eseguo l'evaluate passando l'array di DynamicRoute
                            dynamicRoutes.push(...providerRoute.evaluate([rRoute]));
                        }
                    }
                }

                this.manageRouteConfig(dynamicRoutes);
            }),
            map(()=> of({ success: true })),
            catchError(err => {
                console.log("loadRoutes() - error", err);
                this.manageRouteConfig([]);
                return of({ success: false });
            }),
        );
    }

    private manageRouteConfig(dynamicRoutes: Routes) {
        //  let copyObj = Object.assign({}, obj); -> usato per creare copia dell'oggetto. Assegnazione di una copia del valore e non copia del riferimento.
        let authenticatedRoute = Object.assign({}, this._config.baseAuthenticatedRoute); 
        let baseChildrenAuthenticatedRoute = authenticatedRoute.children as Routes;

        // Aggiungo le rotte ottenute dinamicamente [dynamicRoutes] alle rotte base dell'app. 
        // Queste sono considerate rotte raggiungibili da autenticato.
        authenticatedRoute.children = [
            ...baseChildrenAuthenticatedRoute,
            ...dynamicRoutes
        ];

        const newRouteConfig = [
            ...this._config.baseRoutes,
            authenticatedRoute,
            this._config.defaultRoute
        ];

        // console.log("New route configuration:", this.dynamicRoutesProviderList.length, newRouteConfig);

        this.router.resetConfig(newRouteConfig);
    }
    
    // Get route path from target module name with target route id
    getRoutePath(router: Router, targetModuleName: string, targetRouteId: string): string[] | null {
        const resultFound = this.foundRoutePathFromId(router.config, targetModuleName, targetRouteId);

        // se trovato
        if (resultFound) {
            // rimuovo path vuoti o nulli
            return resultFound.filter(p => p !== '' && p !== null);
        }

        return null;
    }

    // Find path from id 
    private foundRoutePathFromId(
        routes: Route[], 
        targetModuleName: string, 
        targetRouteId: string, 
        paths: string[] = [],
    ): string[] | null {
        // per ogni nodo di route
        for (const route of routes) {
            // se ha providers, cerco quello di identita
            if(route.providers && route.providers.length > 0) {
                // per ogni provider
                for(const provider of route.providers) {
                    // check se è un array di provider o singolo
                    if(Array.isArray(provider)) {
                        // per ognuno controllo se ce il targetRouteId
                        for(const p of provider) {
                            if (p?.['useValue']?.['routeId'] === targetRouteId 
                                && p?.['useValue']?.['moduleName'] === targetModuleName) {
                                    // trovato! restituisco il percorso corrente
                                    return [...paths, route.path ?? ''];
                            }
                        }
                    } else if(provider?.['useValue']?.['routeId'] === targetRouteId 
                        && provider?.['useValue']?.['moduleName'] === targetModuleName) {
                            // trovato! restituisco il percorso corrente
                            return [...paths, route.path ?? ''];
                    }
                }
            }

            // se ha figli: richiamo metodo ricorsivamente aggiungendo frammento di path 
            if(route.children && route.children.length > 0) {
                const found = this.foundRoutePathFromId(route.children, targetModuleName, targetRouteId, [...paths, route.path ?? '']); // todo: modo migliore di -> ?? ''
                // se trovato nella ricorsione lo ritorno
                if(found) {  
                    return found;
                }
            }
        }

        return null; // non trovato
    }

    // Replace path segments with values
    replacePathSegments(pathSegments: string[], values: any[]) : any[] {
        // Se non ci sono values ritorno pathSegment
        if (!values || values.length === 0) {
            return pathSegments;
        }

        let vIndex = 0;

        // Per ogni segment
        return pathSegments.map(segment => {
            // se inizia con : e ci sono ancora valori da sostituire
            if(segment.startsWith(':') && vIndex < values.length) {
                // ritorno il values[i] al posto del segment
                return values[vIndex++];
            }
            return segment;
        });
    }

}